import { MagnifyingGlassIcon } from '@heroicons/react/24/outline'
import { PipeLineType } from 'config'
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { filterPipeline } from 'services'
import { Input2 } from 'stories/components'

import { DEFAULT_LOAN_QUERY_TYPE, LoanQueryType } from './constants'
import { LoanSearchResults } from './LoanSearchResults'
import { getLoanSearchParts, normalizePipelineLoans } from './utils'

const RESULT_LIMIT = 15

/**
 * Form-field variant of GlobalLoanSearch: Input2 + rich loan typeahead.
 * Selecting a loan writes its loan number into `onChange` (for filtering lists).
 * Free-text typing still calls `onChange` so existing text filters keep working.
 */
export const LoanSearchField = ({
  value = '',
  title = 'Search',
  required = false,
  error = '',
  history = false,
  additionalElements,
  onChange,
  onSelectLoan: onSelectLoanProp,
  showHistory,
}: {
  value?: string
  title?: string
  required?: boolean
  error?: string
  history?: boolean
  additionalElements?: JSX.Element | Function | null
  onChange: (value: string) => void
  /** Called when a loan is picked from the typeahead (not on free-text typing). */
  onSelectLoan?: (loan: Record<string, any>) => void
  showHistory?: () => void
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [query, setQuery] = useState(value || '')
  const [queryType, setQueryType] = useState<LoanQueryType>(DEFAULT_LOAN_QUERY_TYPE)
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [loans, setLoans] = useState<Record<string, any>[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)
  const skipSearchRef = useRef(false)
  /**
   * Where to draw the results, in viewport coordinates.
   *
   * They go through a portal rather than sitting in the field as an absolutely positioned child,
   * because this control is used inside Modal: its panel is `overflow-auto` with a max height and
   * its body is `overflow-x-auto`, and setting one overflow axis makes the other a clipping box
   * too. Either one cuts the list off at the modal's edge. A portal to `body` has no such
   * ancestor, so the results are never clipped wherever the field is used.
   */
  const dropdownRef = useRef<HTMLDivElement>(null)
  const [anchor, setAnchor] = useState<{ top: number; left: number; width: number } | null>(null)

  useEffect(() => {
    setQuery(value || '')
  }, [value])

  useEffect(() => {
    if (skipSearchRef.current) {
      skipSearchRef.current = false
      setDebouncedQuery('')
      return
    }
    const timeoutId = setTimeout(() => setDebouncedQuery(query.trim()), 700)
    return () => clearTimeout(timeoutId)
  }, [query])

  useEffect(() => {
    setActiveIndex(0)
  }, [debouncedQuery, loans])

  const searchFilters = useMemo(
    () => ({
      query: debouncedQuery,
      queryType,
      myLoans: false,
      pipelineType: PipeLineType.Default,
      crossPipeline: true,
      orderBy: 'asOf',
      orderDir: '-1',
      skip: 0,
      count: RESULT_LIMIT,
    }),
    [debouncedQuery, queryType],
  )

  useEffect(() => {
    if (!debouncedQuery || !isOpen) {
      setLoans([])
      setTotal(0)
      setLoading(false)
      return
    }

    let cancelled = false
    setLoading(true)

    filterPipeline(searchFilters)
      .then((res) => {
        if (cancelled) return
        setLoans(normalizePipelineLoans(res))
        setTotal(Number(res.total) || 0)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [debouncedQuery, searchFilters, isOpen])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node
      // The results live in a portal and so are NOT inside `containerRef`. Without this, clicking
      // a result would close the list before the click could land on it.
      if (containerRef.current?.contains(target) || dropdownRef.current?.contains(target)) return
      setIsOpen(false)
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  /** Keep the portal under the field as the page, or any scroll container around it, moves. */
  const positionDropdown = useCallback(() => {
    const box = containerRef.current?.getBoundingClientRect()
    if (box) setAnchor({ top: box.bottom, left: box.left, width: box.width })
  }, [])

  useLayoutEffect(() => {
    if (!isOpen) return
    positionDropdown()
    // Capture phase, so this also fires for scrolling inside the modal panel, not just the window.
    window.addEventListener('scroll', positionDropdown, true)
    window.addEventListener('resize', positionDropdown)
    return () => {
      window.removeEventListener('scroll', positionDropdown, true)
      window.removeEventListener('resize', positionDropdown)
    }
  }, [isOpen, positionDropdown])

  const onSelectLoan = (loan: Record<string, any>) => {
    const { loanNo } = getLoanSearchParts(loan)
    const next = String(loanNo || '')
    skipSearchRef.current = true
    setIsOpen(false)
    setLoans([])
    setTotal(0)
    setQuery(next)
    if (onSelectLoanProp) onSelectLoanProp(loan)
    else onChange(next)
  }

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen || !query.trim()) return

    if (event.key === 'ArrowDown') {
      event.preventDefault()
      if (!loans.length) return
      setActiveIndex((prev) => Math.min(prev + 1, loans.length - 1))
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      if (!loans.length) return
      setActiveIndex((prev) => Math.max(prev - 1, 0))
    } else if (event.key === 'Enter') {
      if (loans[activeIndex]) {
        event.preventDefault()
        onSelectLoan(loans[activeIndex])
      }
    } else if (event.key === 'Escape') {
      setIsOpen(false)
    }
  }

  const showResults = isOpen && query.trim().length > 0

  return (
    <div className="LoanSearchField-container relative" ref={containerRef}>
      <Input2
        type="search"
        title={title}
        required={required}
        error={error}
        history={history}
        showHistory={showHistory}
        additionalElements={additionalElements ?? null}
        hasIcon
        icon={<MagnifyingGlassIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />}
        value={query}
        onChange={(nextValue: string) => {
          setQuery(nextValue)
          onChange(nextValue)
          setIsOpen(true)
        }}
        onFocus={() => {
          if (query.trim()) setIsOpen(true)
        }}
        onKeyDown={onKeyDown}
      />
      {showResults &&
        anchor &&
        createPortal(
          <div
            ref={dropdownRef}
            style={{
              position: 'fixed',
              top: anchor.top + 4,
              left: anchor.left,
              width: `min(28rem, max(${Math.round(anchor.width)}px, 18rem))`,
              // Never run off the bottom of the window; the list scrolls instead.
              maxHeight: `calc(100vh - ${Math.round(anchor.top)}px - 1.5rem)`,
            }}
            // Above the modal, which sits at z-50.
            className="z-[100] overflow-y-auto"
          >
            <LoanSearchResults
              query={query}
              queryType={queryType}
              loans={loans}
              total={total}
              loading={loading}
              activeIndex={activeIndex}
              actionHint="to select"
              onSelect={onSelectLoan}
              onQueryTypeChange={setQueryType}
              onActiveIndexChange={setActiveIndex}
            />
          </div>,
          document.body,
        )}
    </div>
  )
}
